/**
 * Mantine component mapping for react-markdown table rendering.
 *
 * Provides consistent table styling when rendering markdown content.
 */

import { Divider, Table, Title } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * Shared component mapping for react-markdown / assistant-ui markdown rendering.
 * Maps Markdown HTML elements to Mantine equivalents (headings, tables, dividers).
 *
 * Note: p/ol/ul/li are intentionally NOT overridden. Previous motion.* wrappers
 * had no animate/initial/exit props, so they paid framer-motion's per-node mount
 * cost without producing any animation — particularly costly during 5-10 Hz
 * message streaming. Plain HTML defaults are used.
 */
export const markdownTableComponents = {
  table({ children }: { children?: ReactNode }) {
    return <Table>{children}</Table>;
  },
  thead({ children }: { children?: ReactNode }) {
    return <Table.Thead>{children}</Table.Thead>;
  },
  tbody({ children }: { children?: ReactNode }) {
    return <Table.Tbody>{children}</Table.Tbody>;
  },
  tr({ children }: { children?: ReactNode }) {
    return <Table.Tr>{children}</Table.Tr>;
  },
  th({ children }: { children?: ReactNode }) {
    return <Table.Th>{children}</Table.Th>;
  },
  td({ children }: { children?: ReactNode }) {
    return <Table.Td>{children}</Table.Td>;
  },
  hr() {
    return <Divider my="md" />;
  },
  h1({ children }: { children?: ReactNode }) {
    return <Title order={1}>{children}</Title>;
  },
  h2({ children }: { children?: ReactNode }) {
    return <Title order={2}>{children}</Title>;
  },
  h3({ children }: { children?: ReactNode }) {
    return <Title order={3}>{children}</Title>;
  },
  h4({ children }: { children?: ReactNode }) {
    return <Title order={4}>{children}</Title>;
  },
  h5({ children }: { children?: ReactNode }) {
    return <Title order={5}>{children}</Title>;
  },
  h6({ children }: { children?: ReactNode }) {
    return <Title order={6}>{children}</Title>;
  },
};
